import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  ArrowBack,
  Gavel as AuctionIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { getProperty } from '../services/propertyService';
import openScheduleService from '../services/openScheduleService';
import AuctionScheduling from '../components/AuctionScheduling';
import type { Property } from '../types/property';
import type { OpenSchedule } from '../services/openScheduleService';

const AuctionSchedulingPage: React.FC = () => {
  const { agencySlug, propertyId } = useParams<{ agencySlug: string; propertyId: string }>();
  const navigate = useNavigate();
  
  const [property, setProperty] = useState<Property | null>(null);
  const [openSchedule, setOpenSchedule] = useState<OpenSchedule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [agencySlug, propertyId]);

  const loadData = async () => {
    if (!agencySlug || !propertyId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Load property details
      const propertyData = await getProperty(agencySlug, propertyId);
      setProperty(propertyData);
      
      // Try to load open schedule to get last opening time
      try {
        const scheduleData = await openScheduleService.getOpenSchedule(propertyId, agencySlug);
        setOpenSchedule(scheduleData);
      } catch (err) {
        // Open schedule might not exist yet, that's okay
        console.log('No open schedule found for this property');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load property data');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToProperty = () => {
    navigate(`/agencies/${agencySlug}/properties/${propertyId}`);
  };

  const handleBackToOpenSchedule = () => {
    navigate(`/agencies/${agencySlug}/properties/${propertyId}/open-schedule`);
  };

  const getLastOpeningTime = () => {
    if (!openSchedule || !openSchedule.sessions || openSchedule.sessions.length === 0) {
      return { date: undefined, time: undefined };
    }
    
    // Find the last scheduled session
    const sortedSessions = [...openSchedule.sessions]
      .filter(session => session.status === 'scheduled' || session.status === 'completed')
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    
    if (sortedSessions.length === 0) {
      return { date: undefined, time: undefined };
    }
    
    const lastSession = sortedSessions[0];
    return {
      date: lastSession.date,
      time: lastSession.startTime
    };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBackToProperty}
        >
          Back to Property
        </Button>
      </Box>
    );
  }

  if (!property) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Property not found
        </Alert>
      </Box>
    );
  }

  const lastOpening = getLastOpeningTime();

  return (
    <Box p={3}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate(`/agencies/${agencySlug}/properties`)}
          sx={{ textDecoration: 'none' }}
        >
          Properties
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={handleBackToProperty}
          sx={{ textDecoration: 'none' }}
        >
          {property.address?.street}, {property.address?.city}
        </Link>
        {openSchedule && (
          <Link
            component="button"
            variant="body1"
            onClick={handleBackToOpenSchedule}
            sx={{ textDecoration: 'none' }}
          >
            Open Schedule
          </Link>
        )}
        <Typography color="text.primary">Auction Scheduling</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={openSchedule ? handleBackToOpenSchedule : handleBackToProperty}
        >
          Back to {openSchedule ? 'Open Schedule' : 'Property'}
        </Button>
        
        <Box display="flex" alignItems="center" gap={1}>
          <AuctionIcon color="primary" />
          <Typography variant="h4">
            Auction Scheduling
          </Typography>
        </Box>
      </Box>

      {/* Property Info */}
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>
          {property.address?.street}, {property.address?.city}, {property.address?.state}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Property ID: {property._id}
        </Typography>
      </Box>

      {/* Auction Scheduling Component */}
      <AuctionScheduling
        property={property}
        agencySlug={agencySlug!}
        onUpdate={loadData}
        defaultToLastOpening={true}
        lastOpeningDate={lastOpening.date}
        lastOpeningTime={lastOpening.time}
      />

      {/* Workflow Navigation */}
      <Box mt={4} p={3} bgcolor="grey.50" borderRadius={1}>
        <Typography variant="h6" gutterBottom>
          Next Steps
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          After scheduling the auction, you can proceed to:
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="outlined"
            onClick={() => navigate(`/agencies/${agencySlug}/properties/${propertyId}/workflow`)}
          >
            View Workflow
          </Button>
          <Button
            variant="outlined"
            onClick={handleBackToProperty}
          >
            Back to Property Details
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AuctionSchedulingPage;
